/**
 * Landing.tsx — GenWizard landing page.
 *
 * Tiles are now gated by the user's *entitlements* (from /me), not a hardcoded
 * is_admin check. Entitlements come from access_config.json on the server:
 *   modules: which of DQ / MDM / DG are visible.
 * Falls back to the old rule (admin → all, else DG) if entitlements are absent,
 * so nothing breaks if the backend hasn't been updated yet.
 */

import React from 'react';
import { injectTheme, injectLandingTheme, T } from './theme/theme';
import AppHeader from './common/AppHeader';
import { useAuth } from './hooks/useAuth';
import { useModuleAccess } from './hooks/useModuleAccess';
import ModuleTile from './common/ModuleTile';
import DQSolutionStory from './common/DQSolutionStory';

// ─── Sub-components ───────────────────────────────────────────────────────────

interface TileProps {
  idx: string;
  icon: string;
  iconBg: string;
  title: string;
  desc: string;
  color: 'cyan' | 'green' | 'amber' | 'purple';
  actions: { label: string; cls: string; onClick: () => void }[];
  extra?: React.ReactNode;              // optional content INSIDE the tile, under its buttons
}

function Tile({ idx, icon, iconBg, title, desc, color, actions, extra }: TileProps) {
  return (
    <div className={`gwl-tile gwl-rise ${color}`}>
      <div className="gwl-tile-header">
        <div className="gwl-tile-icon-wrap" style={{ background: iconBg }}>
          {icon}
        </div>
        <div className="gwl-tile-idx">{idx}</div>
      </div>
      <div className="gwl-tile-title">{title}</div>
      <div className="gwl-tile-desc">{desc}</div>
      <div className="gwl-cta-stack">
        {actions.map((a, i) => (
          <button key={i} className={`gwl-cta ${a.cls}`} onClick={a.onClick}>
            {a.label}
            <span className="gwl-cta-arr">→</span>
          </button>
        ))}
      </div>
      {extra}
    </div>
  );
}

const FEATURES = [
  {
    color: T.brand,
    bg: T.brandLight,
    title: 'AI-Powered Rule Generation',
    desc: 'Profile data and auto-generate DQ rules with confidence scoring',
  },
  {
    color: T.purple,
    bg: T.purpleBg,
    title: 'Governance & Compliance',
    desc: 'Audit-ready policy artifacts aligned to enterprise standards',
  },
  {
    color: T.amber,
    bg: T.amberBg,
    title: 'MDM Modernization Accelerators',
    desc: 'Migrate legacy configurations with AI-guided transformation',
  },
];

// ─── Main ─────────────────────────────────────────────────────────────────────

interface Props {
  setTool: (t: string) => void;
}

export default function GenWizardLanding({ setTool }: Props) {
  const [showStory, setShowStory] = React.useState<null | "DQ" | "DG" | "MDM">(null);   // "How it works" popups

  const { user } = useAuth();

  // Two server-side gates: ENABLED_MODULES (this environment) and the user's
  // entitlement. `stateOf` distinguishes them so a module that is installed but
  // not granted renders disabled, and one that is not installed renders not at
  // all. Fallbacks inside the hook preserve the old behaviour on an older server.
  const { stateOf, visible } = useModuleAccess();
  const tileCount = visible.length || 1;

  const handleRequestAccess = (m: 'DQ' | 'MDM' | 'DG') => {
    const subject = `Knowledge Suite DMG Agents — access request: ${m}`;
    const body =
      `Please grant me access to the ${m} module.\n\n` +
      `User: ${user?.email ?? ''}\nName: ${user?.full_name ?? ''}`;
    window.location.href =
      `mailto:adgo-support@accenture.com?subject=${encodeURIComponent(subject)}` +
      `&body=${encodeURIComponent(body)}`;
  };

  React.useEffect(() => {
    injectTheme();
    injectLandingTheme();
  }, []);

  return (
    <div className="gwl-root"><style>{`.gwl-how{all:unset;cursor:pointer;display:inline-block;margin:10px 0 0 2px;font-size:12.5px;font-weight:700;color:#7500C0}
.gwl-how:hover{text-decoration:underline}`}</style>
      {/* ── Shared header — no app/tool context on landing ── */}
      <AppHeader />

      {/* ── Hero ── */}
      <section className="gwl-hero">
        <div>
          <div className="gwl-hero-eyebrow gwl-hero-rise">Intelligent Data Agents</div>
          <h1 className="gwl-hero-title gwl-hero-rise">
            Every step of the<br />
            <em>data-to-insight</em><br />
            journey for DMG
          </h1>
          <p className="gwl-hero-desc gwl-hero-rise">
            AI-powered governance, data quality, and master data management —
            from raw ingestion to audit-ready enterprise insight.
          </p>
        </div>

        <div className="gwl-hero-right gwl-hero-rise">
          {FEATURES.map((f) => (
            <div key={f.title} className="gwl-feature-item">
              <div
                className="gwl-feature-dot"
                style={{ background: f.color, boxShadow: `0 0 0 3px ${f.bg}` }}
              />
              <div>
                <div className="gwl-feature-title">{f.title}</div>
                <div className="gwl-feature-desc">{f.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Section divider ── */}
      {showStory && <DQSolutionStory kind={showStory} onClose={() => setShowStory(null)} />}

      <div className="gwl-divider">
        <div className="gwl-divider-line" />
        <div className="gwl-divider-label">Mission Modules</div>
        <div className="gwl-divider-line" />
      </div>

      {/* ── Tiles ── */}
      <main
        className="gwl-tiles"
        style={{
          gridTemplateColumns: `repeat(${tileCount}, minmax(0, 1fr))`,
          justifyItems: 'center',
          maxWidth: tileCount === 1 ? 420 : undefined,
          margin: tileCount === 1 ? '0 auto' : undefined,
        }}
      >
        <ModuleTile module="DQ" state={stateOf('DQ')} onRequestAccess={handleRequestAccess}>
          <Tile
            extra={<button className="gwl-how" onClick={(e) => { e.stopPropagation(); setShowStory("DQ"); }}>How it works ▸</button>}
            idx="01 / DQ"
            icon="📏"
            iconBg={T.brandLight}
            title="Define DQ Rules & Governance"
            desc="Identify data quality issues across master and transaction data, finalize validation rules, and shape governance policies aligned with business standards."
            color="cyan"
            actions={[
              {
                label: 'Finalize Rules & Policies',
                cls: 'cyan-c',
                onClick: () => setTool('BUILD_DQ_USING_DATA'),
              },
            ]}
          />
        </ModuleTile>

        <ModuleTile module="MDM" state={stateOf('MDM')} onRequestAccess={handleRequestAccess}>
          <Tile
            extra={<button className="gwl-how" onClick={(e) => { e.stopPropagation(); setShowStory("MDM"); }}>How it works ▸</button>}
            idx="02 / MDM"
            icon="🧠"
            iconBg={T.greenBg}
            title="Master Data Health & Modernization"
            desc="Evaluate the health of your existing master data, detect quality and duplication issues, and receive AI-powered recommendations to modernize your MDM model."
            color="green"
            actions={[
              {
                label: 'Launch E2E Data Management Solution',
                cls: 'green-c',
                onClick: () => setTool('BUILD_MDM_USING_DATA'),
              },
            ]}
          />
        </ModuleTile>

        <ModuleTile module="DG" state={stateOf('DG')} onRequestAccess={handleRequestAccess}>
          <Tile
            extra={<button className="gwl-how" onClick={(e) => { e.stopPropagation(); setShowStory("DG"); }}>How it works ▸</button>}
            idx="03 / GOV"
            icon="🚀"
            iconBg={T.amberBg}
            title="Data Governance"
            desc="Autonomous metadata discovery, explainable compliance, policy-based anonymization, and audit-ready governance artifacts for scalable, trusted enterprise data management."
            color="amber"
            actions={[
              {
                label: 'Launch Governance Suite',
                cls: 'amber-c',
                onClick: () => setTool('BUILD_DG_USING_DATA'),
              },
            ]}
          />
        </ModuleTile>
      </main>

      {/* ── Footer ── */}
      <footer className="gwl-footer">
        <div className="gwl-footer-txt">© 2025 Accenture · All rights reserved</div>
        <div className="gwl-footer-txt">Build 2025.06</div>
      </footer>
    </div>
  );
}